import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import LocatorDropdownProps from "../DropdownProps/LocatorDropdownProps";
import { Button } from "@mui/material";
import { updateActiveLocator } from "../../store/reducers/LocatorsReducers";
import {
  deleteLocatorAction,
  getLocatorsAction,
  updateExistingLocatorAction,
} from "../../store/actions/LocatorsActions";
import Loader from "../Loader";
import SystemMsg from "../SystemMsg";
import { LocatorsDataTypes } from "../../types/LocatorTypes";
import { SuccessCheckmarkIcon } from "../../Icons/Icons";
import LocatorGridNavigation from "./LocatorGridNavigation";
import GridPagination from "./GridPagination";
import { useParams } from "react-router-dom";
import {
  getActiveLocator,
  getLocatorData,
  getLocatorFetchStatus,
  getLocatorFilters,
  getLocatorUpdateStatus,
  getLocatorsAmount,
} from "../../store/selectors/LocatorSelectors";
import { getLocatorsAmountAction } from "../../store/actions/LocatorsActions";

const ITEMS_PER_PAGE: number = 40;

const LocatorsGrid = () => {
  const dispatch = useAppDispatch();
  const { page } = useParams();

  const locatorFetchStatus = useAppSelector(getLocatorFetchStatus);
  const locatorUpdateStatus = useAppSelector(getLocatorUpdateStatus);

  const locators = useAppSelector(getLocatorData);
  const locatorsAmount = useAppSelector(getLocatorsAmount);
  const activeLocator = useAppSelector(getActiveLocator);
  const locatorFilters = useAppSelector(getLocatorFilters);
  const isFetchFailed = locatorFetchStatus === "error";
  const isLocatorsLoading = locatorFetchStatus === "loading";

  const [locatorDraft, setLocatorDraft] = useState<LocatorsDataTypes | null>(
    activeLocator
  );

  const isLocatorUpdateDisabled =
    activeLocator?.lengthid === locatorDraft?.lengthid &&
    activeLocator?.typeid === locatorDraft?.typeid;

  useEffect(() => {
    dispatch(
      getLocatorsAction({
        skip: (Number(page) - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
      })
    );
    dispatch(getLocatorsAmountAction());
  }, [locatorFilters, page, dispatch]);

  useEffect(() => {
    setLocatorDraft(activeLocator);
  }, [activeLocator]);

  const onUpdateLocatorProps = useCallback((props: any) => {
    setLocatorDraft((prev) => ({ ...prev, ...props }));
  }, []);

  const onSaveEditedLocator = useCallback(() => {
    if (!locatorDraft) return;

    dispatch(updateExistingLocatorAction(locatorDraft));
  }, [dispatch, locatorDraft]);

  const onDeleteLocator = useCallback(() => {
    dispatch(
      deleteLocatorAction({
        skip: (Number(page) - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
      })
    );
  }, [page, dispatch]);

  const onCancelEditingLocator = useCallback(() => {
    dispatch(updateActiveLocator(null));
    setLocatorDraft(null);
  }, [dispatch]);

  const renderLocatorItem = (locator: LocatorsDataTypes) => {
    const { locatorid, path, typename } = locator;
    const activeClassName =
      locatorid === locatorDraft?.locatorid ? "active" : "";
    return (
      <div
        className={`locator-cell ${activeClassName}`}
        key={locatorid}
        onClick={() => dispatch(updateActiveLocator(locator))}
      >
        {activeClassName && (
          <span className="checkmark">
            <SuccessCheckmarkIcon />
          </span>
        )}
        <img src={path} alt={typename} />
      </div>
    );
  };

  const locatorItems =
    locators?.length === 0 ? (
      <SystemMsg message="No data with selected filters" messageType="error" />
    ) : (
      locators?.map((locator) => renderLocatorItem(locator))
    );

  const renderLocatorDetails = useCallback(() => {
    if (!activeLocator) return <></>;

    return (
      <>
        <div className="locator-details">
          <h2>Data labels</h2>
          <LocatorDropdownProps
            onChooseLocatorType={(evt: any) => {
              onUpdateLocatorProps({ typeid: evt.target.value });
            }}
            onChooseLocatorLength={(evt: any) =>
              onUpdateLocatorProps({ lengthid: evt.target.value })
            }
            selectedLocatorType={locatorDraft?.typeid}
            selectedLocatorLength={locatorDraft?.lengthid}
          />
        </div>
        <div className="locator-actions">
          <div className="row">
            <Button
              className="btn btn-blue"
              variant="contained"
              disabled={isLocatorUpdateDisabled}
              onClick={onSaveEditedLocator}
            >
              Save changes
            </Button>
          </div>
          <div className="row">
            <Button
              className="btn btn-danger"
              variant="contained"
              onClick={onDeleteLocator}
            >
              Delete
            </Button>
          </div>
          <div className="row">
            <Button
              className="btn btn-outlined"
              variant="outlined"
              onClick={onCancelEditingLocator}
            >
              Cancel
            </Button>
          </div>
        </div>
      </>
    );
  }, [
    activeLocator,
    isLocatorUpdateDisabled,
    locatorDraft,
    onCancelEditingLocator,
    onUpdateLocatorProps,
    onSaveEditedLocator,
    onDeleteLocator,
  ]);

  const locatorDetailsContent =
    locatorUpdateStatus === "loading" ? (
      <Loader />
    ) : locatorUpdateStatus === "error" ? (
      <SystemMsg />
    ) : locatorUpdateStatus === "success" ? (
      <SystemMsg messageType="success" message="Success" />
    ) : (
      renderLocatorDetails()
    );

  const locatorDataGrid = isFetchFailed ? (
    <SystemMsg />
  ) : (
    <div className="locators-grid">
      <div className="left-side">
        <LocatorGridNavigation />
        <div className="grid">{locatorItems}</div>
        <GridPagination totalItems={locatorsAmount} baseRoute="/dataset/locators" />
      </div>
      <div className="right-side">{locatorDetailsContent}</div>
    </div>
  );

  return isLocatorsLoading ? (
    <Loader />
  ) : isFetchFailed ? (
    <SystemMsg messageType="error" message="Failed to load locators data" />
  ) : locators ? (
    locatorDataGrid
  ) : (
    <SystemMsg messageType="error" message="No locators data available" />
  );
};

export default LocatorsGrid;
